/**
 * useContentTranslation Hook - Dynamic content translation with caching
 * 
 * Translates user-generated content (titles, descriptions) on-the-fly
 * when the UI language differs from the content's original language.
 * 
 * Features:
 * - Detects original content language
 * - Translates via AI API when needed
 * - Caches translations in memory and localStorage
 * - Updates immediately on language switch
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;

// In-memory cache for translations (faster than localStorage for current session)
const translationCache = new Map();

// Cache key generator
const getCacheKey = (text, targetLang) => {
  // Use first 50 chars + length as a simple hash
  const textKey = `${text.substring(0, 50)}_${text.length}`;
  return `${textKey}_${targetLang}`;
};

// Detect language from text (simple heuristic based on character sets)
const detectLanguage = (text) => {
  if (!text || text.length < 3) return 'en';
  
  // Check for Cyrillic (Russian, Ukrainian, etc.)
  if (/[\u0400-\u04FF]/.test(text)) return 'ru';
  
  // Check for Estonian specific characters
  if (/[õäöüÕÄÖÜ]/.test(text) && /[^åÅ]/.test(text)) return 'et';
  
  // Check for Finnish/Swedish (has ä, ö but also å)
  if (/[åÅ]/.test(text)) {
    if (/[äöÄÖ]/.test(text)) return 'fi'; // Finnish has both
    return 'sv';
  }
  
  // Check for Nordic languages (Norwegian, Danish)
  if (/[æøåÆØÅ]/.test(text)) return 'nb';
  
  // Check for German specific
  if (/[ß]/.test(text) || /[äöüÄÖÜ]/.test(text) && /\b(der|die|das|und|ist)\b/i.test(text)) return 'de';
  
  // Check for Chinese
  if (/[\u4E00-\u9FFF]/.test(text)) return 'zh';
  
  // Check for Japanese
  if (/[\u3040-\u30FF]/.test(text)) return 'ja';
  
  // Check for Korean
  if (/[\uAC00-\uD7AF]/.test(text)) return 'ko';
  
  // Check for Arabic
  if (/[\u0600-\u06FF]/.test(text)) return 'ar';
  
  // Check for Hebrew
  if (/[\u0590-\u05FF]/.test(text)) return 'he';
  
  // Default to English
  return 'en';
};

// Translate text via API
const translateText = async (text, sourceLang, targetLang) => {
  try {
    const response = await fetch(`${API}/api/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text,
        source_language: sourceLang,
        target_language: targetLang
      })
    });
    
    if (!response.ok) {
      console.warn('[Translation] API error:', response.status);
      return null;
    }
    
    const data = await response.json();
    return data.translated_text;
  } catch (error) {
    console.error('[Translation] Failed:', error);
    return null;
  }
};

/**
 * Hook to translate a single piece of content
 * 
 * @param {string} originalText - The original text content
 * @param {string} originalLang - Optional: known original language code
 * @returns {object} - { text, isTranslated, isLoading, originalLang }
 */
export const useTranslatedContent = (originalText, originalLang = null) => {
  const { language: uiLanguage } = useLanguage();
  const [translatedText, setTranslatedText] = useState(originalText);
  const [isLoading, setIsLoading] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);
  const [detectedLang, setDetectedLang] = useState(originalLang);
  
  // Track if component is mounted
  const isMounted = useRef(true);
  
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
  
  useEffect(() => {
    if (!originalText || originalText.length < 5) {
      setTranslatedText(originalText);
      setIsTranslated(false);
      return;
    }
    
    // Detect or use provided original language
    const sourceLang = originalLang || detectLanguage(originalText);
    setDetectedLang(sourceLang);
    
    // If same language, no translation needed
    if (sourceLang === uiLanguage) {
      setTranslatedText(originalText);
      setIsTranslated(false);
      return;
    }
    
    // Check cache first
    const cacheKey = getCacheKey(originalText, uiLanguage);
    const cached = translationCache.get(cacheKey);
    
    if (cached) {
      setTranslatedText(cached);
      setIsTranslated(true);
      return;
    }
    
    // Check localStorage cache
    try {
      const localCached = localStorage.getItem(`trans_${cacheKey}`);
      if (localCached) {
        translationCache.set(cacheKey, localCached);
        setTranslatedText(localCached);
        setIsTranslated(true);
        return;
      }
    } catch (e) {
      // localStorage might be unavailable
    }
    
    // Need to translate - show original while loading
    setTranslatedText(originalText);
    setIsLoading(true);
    
    translateText(originalText, sourceLang, uiLanguage)
      .then(result => {
        if (!isMounted.current) return;
        
        if (result) {
          // Cache the result
          translationCache.set(cacheKey, result);
          try {
            localStorage.setItem(`trans_${cacheKey}`, result);
          } catch (e) {
            // localStorage might be full
          }
          
          setTranslatedText(result);
          setIsTranslated(true);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setIsLoading(false);
        }
      });
  }, [originalText, originalLang, uiLanguage]);
  
  return {
    text: translatedText,
    isTranslated,
    isLoading,
    originalLang: detectedLang,
    originalText
  };
};

/**
 * Hook to translate multiple content items (for lists)
 * 
 * @param {Array} items - Array of items with text fields
 * @param {Array} fields - Field names to translate ['title', 'description']
 * @returns {object} - { items, isLoading }
 */
export const useTranslatedList = (items, fields = ['title', 'description']) => {
  const { language: uiLanguage } = useLanguage();
  const [translatedItems, setTranslatedItems] = useState(items);
  const [isLoading, setIsLoading] = useState(false);
  const pendingRef = useRef(new Set());
  
  useEffect(() => {
    if (!items || items.length === 0) {
      setTranslatedItems([]);
      return;
    }
    
    // Start with original items
    setTranslatedItems(items);
    
    // Collect items that need translation
    const toTranslate = [];
    
    items.forEach((item, index) => {
      fields.forEach(field => {
        const text = item[field];
        if (!text || text.length < 5) return;
        
        const sourceLang = detectLanguage(text);
        if (sourceLang === uiLanguage) return;
        
        const cacheKey = getCacheKey(text, uiLanguage);
        const cached = translationCache.get(cacheKey);
        
        if (cached) {
          // Apply cached translation immediately
          setTranslatedItems(prev => {
            const updated = [...prev];
            if (updated[index]) {
              updated[index] = {
                ...updated[index],
                [field]: cached,
                [`${field}_original`]: text,
                [`${field}_translated`]: true
              };
            }
            return updated;
          });
        } else {
          toTranslate.push({ index, field, text, sourceLang, cacheKey });
        }
      });
    });
    
    // Translate missing items
    if (toTranslate.length > 0) {
      setIsLoading(true);
      
      // Batch translate (limit concurrent requests)
      const batchSize = 5;
      const batches = [];
      for (let i = 0; i < toTranslate.length; i += batchSize) {
        batches.push(toTranslate.slice(i, i + batchSize));
      }
      
      const translateBatch = async (batch) => {
        const promises = batch.map(async ({ index, field, text, sourceLang, cacheKey }) => {
          const result = await translateText(text, sourceLang, uiLanguage);
          if (result) {
            translationCache.set(cacheKey, result);
            try {
              localStorage.setItem(`trans_${cacheKey}`, result);
            } catch (e) {}
            
            setTranslatedItems(prev => {
              const updated = [...prev];
              if (updated[index]) {
                updated[index] = {
                  ...updated[index],
                  [field]: result,
                  [`${field}_original`]: text,
                  [`${field}_translated`]: true
                };
              }
              return updated;
            });
          }
        });
        
        await Promise.all(promises);
      };
      
      // Process batches sequentially to avoid rate limiting
      (async () => {
        for (const batch of batches) {
          await translateBatch(batch);
        }
        setIsLoading(false);
      })();
    }
  }, [items, fields, uiLanguage]);
  
  return { items: translatedItems, isLoading };
};

/**
 * Clear translation cache (useful for testing or when storage is full)
 */
export const clearTranslationCache = () => {
  translationCache.clear();
  
  // Clear localStorage cache
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('trans_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {}
};

export default useTranslatedContent;
